<template>
  <div v-if="loading" class="w-full h-full flex items-center justify-center">
    <NLoading class="text-brand-primary" />
  </div>

  <div v-else class="flex flex-col">
    <NText variant="caption1" weight="bold" class="mb-4 pr-4">
      {{ t('modules.portfolio.embeddedPutOption.apply') }}
    </NText>

    <VeeForm v-slot="{ meta, handleSubmit, errors }" as="">
      <form autocomplete="off" @submit="handleSubmit($event, onSubmit)">
        <div class="bg-neutral-secondary p-4 rounded-md mt-1 w-[21.5rem]">
          <VeeField
            v-slot="{ field }"
            v-model="formModel.requestType"
            name="requestType"
            rules="required"
          >
            <NRadioGroup
              v-model="formModel.requestType"
              required
              v-bind="field"
              :error="errors.requestType"
              class="pb-1"
            >
              <div class="flex flex-col bg-neutral-primary rounded-md">
                <NRadio
                  v-for="{ value, title } in Object.values(
                    EMBEDDED_PUT_OPTION_REQUEST_TYPES
                  )"
                  :key="value"
                  :value="value"
                  class="w-full relative border-b border-neutral-tertiary last:border-0 p-3"
                >
                  <div class="flex justify-between text-neutral-primary">
                    <NText
                      variant="caption1"
                      weight="bold"
                      class="text-neutral-secondary-press"
                    >
                      {{ title }}
                    </NText>
                  </div>
                </NRadio>
              </div>
            </NRadioGroup>
          </VeeField>

          <div
            v-if="
              formModel.requestType ===
              EMBEDDED_PUT_OPTION_REQUEST_TYPES.selective.value
            "
            class="flex flex-col"
          >
            <NText
              variant="caption1"
              weight="medium"
              class="text-neutral-secondary-press block pr-3 mb-2 mt-4"
            >
              {{ t('modules.portfolio.embeddedPutOption.physicalCount') }}
            </NText>

            <VeeField
              v-slot="{ field }"
              v-model="formModel.physicalRequestCount"
              name="physicalRequestCount"
              :rules="{
                required: true,
                min: [1],
              }"
            >
              <NInput
                v-model="formModel.physicalRequestCount"
                v-mask="{
                  alias: 'positiveNumber',
                  showMaskOnHover: false,
                }"
                :placeholder="
                  t('modules.portfolio.embeddedPutOption.insertQuantity')
                "
                type="tel"
                input-class="text-right"
                v-bind="field"
                :error="errors.physicalRequestCount"
                :show-error="true"
                :required-sign="false"
                dir="ltr"
                required
                @focusin="$event.target.select()"
              />
            </VeeField>

            <NText
              variant="caption1"
              weight="medium"
              class="text-neutral-secondary-press block pr-3 mb-2 mt-4"
            >
              {{
                t(
                  'modules.portfolio.embeddedPutOption.maxCashThenPhysicalCount'
                )
              }}
            </NText>

            <VeeField
              v-slot="{ field }"
              v-model="formModel.firstCashRequestCount"
              name="firstCashRequestCount"
              :rules="{
                required: true,
                min: [1],
              }"
            >
              <NInput
                v-model="formModel.firstCashRequestCount"
                v-mask="{
                  alias: 'positiveNumber',
                  showMaskOnHover: false,
                }"
                :placeholder="
                  t('modules.portfolio.embeddedPutOption.insertQuantity')
                "
                type="tel"
                :input-class="['text-right']"
                v-bind="field"
                :error="errors.firstCashRequestCount"
                :show-error="true"
                :required-sign="false"
                dir="ltr"
                required
                @focusin="$event.target.select()"
              />
            </VeeField>
          </div>

          <VeeField
            v-if="!isEdit"
            v-slot="{ field }"
            v-model="hasAcceptedConditions"
            name="firstCashRequestCount"
            required
            rules="required"
          >
            <NCheckbox
              v-bind="field"
              v-model="hasAcceptedConditions"
              requied
              class="mt-3"
            >
              <NText
                weight="medium"
                variant="caption1"
                class="text-neutral-secondary-hover"
              >
                <i18n-t
                  scope="global"
                  keypath="modules.portfolio.embeddedPutOption.acceptConditionMessage"
                  tag="span"
                >
                  <template #embeddedPutOptionTitle>
                    <a
                      target="_blank"
                      href="/docs/embedded-put-option-guide.pdf"
                    >
                      <NText
                        variant="caption1"
                        weight="bold"
                        class="text-brand-primary"
                      >
                        {{ t('modules.portfolio.embeddedPutOption.title') }}
                      </NText>
                    </a>
                  </template>
                </i18n-t>
              </NText>
            </NCheckbox>
          </VeeField>
        </div>

        <div class="w-full flex mt-6">
          <NButton
            :disabled="!meta.valid || !hasAcceptedConditions"
            class="flex-[1] ml-6"
            type="submit"
          >
            {{ isEdit ? t('common.edit') : t('misc.submit') }}
          </NButton>

          <NButton
            color="gray"
            variant="plain"
            class="flex-[1]"
            type="button"
            @click="removeDialog()"
          >
            {{ t('common.cancel') }}
          </NButton>
        </div>
      </form>
    </VeeForm>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast, useDialog } from '@negar/core';
import {
  EMBEDDED_PUT_OPTION_REQUEST_TYPES,
  PORTFOLIO_QUERY_KEYS,
  useCreateEmbeddedPutOptionRequestQuery,
  useGetEmbeddedPutOptionSymbolInfoQuery,
  useUpdateEmbeddedPutOptionRequestQuery,
} from '@core/portfolio';
import { EmbeddedPutOptionSymbol } from '@libs/api-reference';
import { useQueryClient } from '@libs/use';

import vMask from '@/directives/mask';

type EmbeddedPutOptionFormPropsType = {
  symbol: EmbeddedPutOptionSymbol;
  isEdit?: boolean;
};

const props = defineProps<EmbeddedPutOptionFormPropsType>();

const FORM_MODEL_BASE = {
  isin: props.symbol.instrumentIsin,
  clientCode: props.symbol.clientCode,
  requestType: 1,
  physicalRequestCount: 0,
  firstCashRequestCount: 0,
};

const hasAcceptedConditions = ref<boolean>(Boolean(props.isEdit));
const isin = ref<string>(props.symbol.instrumentIsin || '');
const loading = ref<boolean>(Boolean(props.isEdit));

const formModel = reactive(FORM_MODEL_BASE);

const typeTitle = computed(
  () =>
    Object.values(EMBEDDED_PUT_OPTION_REQUEST_TYPES).find(
      (requestType) => requestType.value === formModel.requestType
    )?.title
);

const { t } = useI18n();

const queryClient = useQueryClient();

const { remove: removeDialog } = useDialog();
const toast = useToast();

const {
  embeddedPutOptionSymbolInfo,
  isSuccessLoadingEmbeddedPutOptionSymbolInfo,
  isRevalidatingEmbeddedPutOptionSymbolInfo,
} = useGetEmbeddedPutOptionSymbolInfoQuery({
  isin,
  immediate: props.isEdit,
});

const { createEmbeddedPutOptionRequest } =
  useCreateEmbeddedPutOptionRequestQuery({
    onSuccess: () => {
      toast.neutral({
        message: t('modules.portfolio.embeddedPutOption.createSuccessMessage', {
          instrumentName: props.symbol.instrumentName,
          type: typeTitle.value,
        }),
        duration: 3000,
      });

      Object.assign(formModel, FORM_MODEL_BASE);

      queryClient.invalidateQueries(
        PORTFOLIO_QUERY_KEYS.embeddedPutOptionSymbols
      );

      removeDialog();
    },
    onReject: () => {
      toast.negative({
        message: t('modules.portfolio.embeddedPutOption.createErrorMessage', {
          instrumentName: props.symbol.instrumentName,
          type: typeTitle.value,
        }),
        duration: 3000,
      });
    },
  });

const { updateEmbeddedPutOptionRequest } =
  useUpdateEmbeddedPutOptionRequestQuery({
    isin: props.symbol.instrumentIsin || '',
    onSuccess: () => {
      toast.neutral({
        message: t('modules.portfolio.embeddedPutOption.createSuccessMessage', {
          instrumentName: props.symbol.instrumentName,
          type: typeTitle.value,
        }),
        duration: 3000,
      });

      queryClient.invalidateQueries(
        PORTFOLIO_QUERY_KEYS.embeddedPutOptionSymbols
      );

      removeDialog();
    },
    onReject: () => {
      toast.negative({
        message: t('modules.portfolio.embeddedPutOption.createErrorMessage', {
          instrumentName: props.symbol.instrumentName,
          type: typeTitle.value,
        }),
        duration: 3000,
      });
    },
  });

const onSubmit = () => {
  if (props.isEdit) {
    updateEmbeddedPutOptionRequest({ formModel });
  } else {
    createEmbeddedPutOptionRequest({ formModel });
  }
};

watch(
  [
    isSuccessLoadingEmbeddedPutOptionSymbolInfo,
    isRevalidatingEmbeddedPutOptionSymbolInfo,
  ],
  () => {
    if (isSuccessLoadingEmbeddedPutOptionSymbolInfo.value) {
      if (props.isEdit) {
        Object.assign(formModel, {
          ...formModel,
          physicalRequestCount:
            embeddedPutOptionSymbolInfo.value?.physicalRequestCount,
          firstCashRequestCount:
            embeddedPutOptionSymbolInfo.value?.firstCashRequestCount,
          requestType: embeddedPutOptionSymbolInfo.value?.requestType,
        });

        loading.value = false;
      }
    }
  }
);
</script>
